import { useEffect, useMemo, useState } from "react";
import { FaFolder, FaGlobe, FaPlus, FaSearch } from "react-icons/fa";
import AppIcon from "../AppIcon";
import { useProfileEditorAppCatalog } from "../../hooks/useProfileEditorAppCatalog";
import { BUILTIN_IOS_APPS, canonicalIosAppBundleId } from "./builtinIosApps";

const IPAD_PAGE_SLOTS = { comfortable: 20, dense: 30 };
const DOCK_SLOTS = 6;
const DRAG_MIME = "application/x-xavier-homescreen";

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

function itemKey(item, fallback) {
  if (!item) return fallback;
  if (item.Type === "Application") return `app:${item.BundleID || fallback}`;
  if (item.Type === "WebClip") return `web:${item.URL || fallback}`;
  if (item.Type === "Folder") return `folder:${item.DisplayName || fallback}`;
  return fallback;
}

function itemLabel(item, appsById) {
  if (!item) return "";
  if (item.Type === "Folder") return item.DisplayName || "Ordner";
  if (item.Type === "WebClip") return item.Label || item.URL || "Webclip";
  return appsById.get(item.BundleID)?.name || item.BundleID || "App";
}

function itemArtwork(item, appsById) {
  if (!item || item.Type !== "Application") return "";
  return appsById.get(item.BundleID)?.artworkUrl || "";
}

export function collectLayoutBundleIds(fields) {
  const ids = new Set();
  const visit = (entry) => {
    if (!entry) return;
    if (entry.Type === "Application" && entry.BundleID) {
      ids.add(canonicalIosAppBundleId(entry.BundleID));
    }
    if (entry.Type === "Folder") {
      for (const page of asArray(entry.Pages)) {
        for (const child of asArray(page)) visit(child);
      }
    }
  };
  for (const item of asArray(fields?.Dock)) visit(item);
  for (const page of asArray(fields?.Pages)) {
    for (const item of asArray(page)) visit(item);
  }
  ids.add("com.apple.Preferences");
  return [...ids];
}

function Slot({ item, appsById, onDropItem, onRemove, onOpenFolder, onPlace, selected }) {
  return (
    <button
      type="button"
      className={`ipad-slot ${item ? "" : "is-empty"} ${selected ? "is-target" : ""}`}
      title={item ? `${itemLabel(item, appsById)} — klicken zum Entfernen` : "Leerer Platz — App hierher ziehen oder nach Auswahl antippen"}
      onDragOver={(event) => {
        event.preventDefault();
        event.currentTarget.classList.add("is-target");
      }}
      onDragLeave={(event) => event.currentTarget.classList.remove("is-target")}
      onDrop={(event) => {
        event.preventDefault();
        event.currentTarget.classList.remove("is-target");
        const raw = event.dataTransfer.getData(DRAG_MIME) || event.dataTransfer.getData("text/plain");
        if (!raw) return;
        try {
          onDropItem(JSON.parse(raw));
        } catch {
          /* ignore */
        }
      }}
      onClick={() => {
        if (item?.Type === "Folder") onOpenFolder?.(item);
        else if (item) onRemove();
        else onPlace?.();
      }}
    >
      {item ? (
        item.Type === "Folder" ? (
          <div
            className="d-flex align-items-center justify-content-center bg-secondary-subtle"
            style={{ width: 36, height: 36, borderRadius: 8 }}
          >
            <FaFolder title="Ordner" />
          </div>
        ) : item.Type === "WebClip" ? (
          <div
            className="d-flex align-items-center justify-content-center bg-white"
            style={{ width: 36, height: 36, borderRadius: 8 }}
          >
            <FaGlobe title={item.URL || "Webclip"} />
          </div>
        ) : (
          <AppIcon src={itemArtwork(item, appsById)} name={itemLabel(item, appsById)} size={36} />
        )
      ) : null}
      <span className="ipad-slot-name">{item ? itemLabel(item, appsById) : ""}</span>
    </button>
  );
}

function PickerTile({ item, selected, onSelect }) {
  return (
    <button
      type="button"
      className={`app-picker-item ${selected ? "is-selected" : ""}`}
      draggable
      title={item.subtitle || item.name}
      onDragStart={(event) => {
        event.dataTransfer.setData(DRAG_MIME, JSON.stringify(item.layoutItem));
        event.dataTransfer.effectAllowed = "copy";
      }}
      onClick={() => onSelect(item)}
    >
      {item.kind === "folder" ? (
        <div className="d-flex align-items-center justify-content-center bg-light border" style={{ width: 44, height: 44, borderRadius: 10 }}>
          <FaFolder />
        </div>
      ) : item.kind === "webclip" ? (
        <div className="d-flex align-items-center justify-content-center bg-light border" style={{ width: 44, height: 44, borderRadius: 10 }}>
          <FaGlobe />
        </div>
      ) : (
        <AppIcon src={item.artworkUrl} name={item.name} size={44} />
      )}
      <span>{item.name}</span>
    </button>
  );
}

export default function HomeScreenLayoutEditor({
  fields,
  onChange,
  disabled,
  webclips = [],
  availableApps = null,
  hideWebclips = false,
  hideAllowlistOption = false,
}) {
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("apps");
  const [pageIndex, setPageIndex] = useState(0);
  const [dense, setDense] = useState(false);
  const [selected, setSelected] = useState(null);
  const [folderEdit, setFolderEdit] = useState(null);

  const layoutBundleIds = useMemo(() => collectLayoutBundleIds(fields), [fields]);
  const extraIds = useMemo(() => {
    if (!Array.isArray(availableApps)) return layoutBundleIds;
    return [...new Set([...layoutBundleIds, ...availableApps.map((a) => a.bundleId).filter(Boolean)])];
  }, [layoutBundleIds, availableApps]);
  const { appsById, vppApps } = useProfileEditorAppCatalog(extraIds);

  const dock = asArray(fields?.Dock);
  const pages = asArray(fields?.Pages).length ? asArray(fields?.Pages).map(asArray) : [[]];
  const pageSlots = dense ? IPAD_PAGE_SLOTS.dense : IPAD_PAGE_SLOTS.comfortable;
  const columns = dense ? 6 : 5;
  const currentPage = pages[pageIndex] || [];

  const pickerApps = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = (name, id) => !q || name.toLowerCase().includes(q) || id.toLowerCase().includes(q);
    if (Array.isArray(availableApps)) {
      const installed = availableApps
        .filter((app) => app?.bundleId && matches(app.name || app.bundleId, app.bundleId))
        .map((app) => ({
          id: app.bundleId,
          name: app.name || appsById.get(app.bundleId)?.name || app.bundleId,
          subtitle: app.deviceCount ? `auf ${app.deviceCount} Gerät(en)` : app.bundleId,
          artworkUrl: appsById.get(app.bundleId)?.artworkUrl || "",
          kind: "app",
          group: "installed",
          layoutItem: { Type: "Application", BundleID: app.bundleId },
        }));
      return { system: installed, catalog: [] };
    }
    const system = BUILTIN_IOS_APPS.filter((app) => matches(app.name, app.bundleId)).map((app) => ({
      id: app.bundleId,
      name: app.name,
      subtitle: app.bundleId,
      artworkUrl: appsById.get(app.bundleId)?.artworkUrl || "",
      kind: "app",
      group: "system",
      layoutItem: { Type: "Application", BundleID: app.bundleId },
    }));
    const builtinIds = new Set(BUILTIN_IOS_APPS.map((app) => app.bundleId));
    const catalog = vppApps
      .filter((app) => app.bundleId && !builtinIds.has(app.bundleId) && matches(app.name || app.bundleId, app.bundleId))
      .map((app) => ({
        id: app.bundleId,
        name: app.name || app.bundleId,
        subtitle: app.bundleId,
        artworkUrl: app.artworkUrlSmall || app.artworkUrl || "",
        kind: "app",
        group: "catalog",
        layoutItem: { Type: "Application", BundleID: app.bundleId },
      }));
    return { system, catalog };
  }, [query, vppApps, appsById, availableApps]);

  const pickerWebclips = useMemo(() => {
    const q = query.trim().toLowerCase();
    return webclips
      .map((clip) => ({
        id: clip.URL,
        name: clip.Label || clip.URL,
        subtitle: clip.URL,
        kind: "webclip",
        layoutItem: { Type: "WebClip", URL: clip.URL, Label: clip.Label },
      }))
      .filter((clip) => clip.id && (!q || clip.name.toLowerCase().includes(q) || clip.id.toLowerCase().includes(q)));
  }, [query, webclips]);

  const commit = (nextDock, nextPages, extra = {}) => {
    onChange({
      ...fields,
      Dock: nextDock,
      Pages: nextPages,
      ...extra,
    });
  };

  const placeOnPage = (item, slotIndex) => {
    const nextPages = pages.map((page) => page.filter(Boolean));
    while (nextPages.length <= pageIndex) nextPages.push([]);
    const page = [...(nextPages[pageIndex] || [])];
    const existing = page[slotIndex];
    if (existing?.Type === "Folder" && item.Type && item.Type !== "Folder") {
      const folderPages = Array.isArray(existing.Pages) && existing.Pages.length ? existing.Pages.map((row) => [...row]) : [[]];
      folderPages[0] = [...(folderPages[0] || []), item];
      page[slotIndex] = { ...existing, Pages: folderPages };
    } else if (slotIndex >= page.length) {
      page.push(item);
    } else {
      page[slotIndex] = item;
    }
    nextPages[pageIndex] = page.slice(0, pageSlots);
    commit(dock.filter(Boolean), nextPages);
    setSelected(null);
  };

  const placeInDock = (item, slotIndex) => {
    const next = dock.filter(Boolean);
    if (slotIndex >= next.length) next.push(item);
    else next[slotIndex] = item;
    commit(next.slice(0, DOCK_SLOTS), pages.map((page) => page.filter(Boolean)));
    setSelected(null);
  };

  const handleSlotDrop = (target, slotIndex, incoming) => {
    if (disabled || !incoming?.Type) return;
    const item = incoming.Type === "Application" && incoming.BundleID
      ? { ...incoming, BundleID: canonicalIosAppBundleId(incoming.BundleID) }
      : incoming;
    if (selected) setSelected(null);
    if (target === "dock") placeInDock(item, slotIndex);
    else placeOnPage(item, slotIndex);
  };

  const handleSlotClickPlace = (target, slotIndex) => {
    if (disabled) return;
    if (selected?.layoutItem) {
      handleSlotDrop(target, slotIndex, selected.layoutItem);
      return;
    }
  };

  const addPage = () => {
    commit(dock, [...pages, []]);
    setPageIndex(pages.length);
  };

  const addFolder = () => {
    const name = window.prompt("Ordnername", "Gruppe") || "Gruppe";
    setSelected({
      id: `folder-${Date.now()}`,
      name,
      kind: "folder",
      layoutItem: { Type: "Folder", DisplayName: name, Pages: [[]] },
    });
  };

  return (
    <div>
      <p className="profile-hint-once mb-2">
        Dock und Seiten gelten für betreute iPhones und iPads (iOS 9.3+). Am iPad ist das Dock Teil dieses Payloads.
        Im größeren iPad-Raster sind 20 Apps pro Seite sichtbar, im dichten Raster 30.
      </p>
      <div className="ipad-layout">
        <div>
          <div className="ipad-frame">
            <div className="ipad-screen">
              <div className={`ipad-grid ${dense ? "is-dense" : ""}`} style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
                {Array.from({ length: pageSlots }, (_, index) => {
                  const item = currentPage[index] || null;
                  return (
                    <Slot
                      key={`p-${index}`}
                      item={item}
                      appsById={appsById}
                      selected={Boolean(selected) && !item}
                      onOpenFolder={(folder) => setFolderEdit({ ...folder, pageIndex, slotIndex: index })}
                      onRemove={() => {
                        if (disabled) return;
                        const next = pages.map((page) => [...page.filter(Boolean)]);
                        next[pageIndex] = (next[pageIndex] || []).filter((_, slot) => slot !== index);
                        commit(dock.filter(Boolean), next);
                      }}
                      onPlace={() => handleSlotClickPlace("page", index)}
                      onDropItem={(incoming) => handleSlotDrop("page", index, incoming)}
                    />
                  );
                })}
              </div>
              <div className="ipad-dots">
                {pages.map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`ipad-dot ${index === pageIndex ? "is-active" : ""}`}
                    aria-label={`Seite ${index + 1}`}
                    onClick={() => setPageIndex(index)}
                  />
                ))}
              </div>
              <div className="ipad-dock">
                {Array.from({ length: DOCK_SLOTS }, (_, index) => {
                  const item = dock[index] || null;
                  return (
                    <Slot
                      key={`d-${index}`}
                      item={item}
                      appsById={appsById}
                      selected={Boolean(selected) && !item}
                      onRemove={() => {
                        if (disabled) return;
                        commit(dock.filter(Boolean).filter((_, slot) => slot !== index), pages.map((page) => page.filter(Boolean)));
                      }}
                      onPlace={() => handleSlotClickPlace("dock", index)}
                      onDropItem={(incoming) => handleSlotDrop("dock", index, incoming)}
                    />
                  );
                })}
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 mt-2">
            <button type="button" className="btn btn-outline-secondary btn-sm" disabled={disabled} onClick={addPage}>
              Seite hinzufügen
            </button>
            <label className="small d-flex align-items-center gap-1 mb-0">
              <input type="checkbox" checked={dense} onChange={(e) => setDense(e.target.checked)} />
              Dichtes Raster (30)
            </label>
          </div>
          {!hideAllowlistOption ? (
            <>
              <label className="form-check mt-2">
                <input
                  type="checkbox"
                  className="form-check-input"
                  disabled={disabled}
                  checked={Boolean(fields?.syncAppAccessWithLayout)}
                  onChange={(e) => onChange({ ...fields, syncAppAccessWithLayout: e.target.checked || undefined })}
                />
                <span className="form-check-label">
                  Positivliste in den Einschränkungen aus diesem Layout setzen
                </span>
              </label>
              <div className="form-text">
                Schreibt die Bundle-IDs der platzierten Apps plus Einstellungen in die Allow-List. Webclips bleiben eigene Payloads.
              </div>
            </>
          ) : null}
        </div>

        <div>
          <div className="input-group input-group-sm mb-2">
            <span className="input-group-text"><FaSearch /></span>
            <input
              className="form-control"
              placeholder="Suchen…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <div className="btn-group mb-2" role="group">
            <button type="button" className={`btn btn-sm ${tab === "apps" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("apps")}>
              Apps
            </button>
            {!hideWebclips ? (
              <button type="button" className={`btn btn-sm ${tab === "webclips" ? "btn-primary" : "btn-outline-primary"}`} onClick={() => setTab("webclips")}>
                Webclips
              </button>
            ) : null}
          </div>
          {tab === "apps" ? (
            <>
              <button type="button" className="btn btn-outline-secondary btn-sm mb-2" disabled={disabled} onClick={addFolder}>
                <FaPlus className="me-1" /> Neue Gruppe
              </button>
              <div className="profile-restriction-group">
                {Array.isArray(availableApps) ? "Installierte Apps (Schüler)" : "System-Apps"}
              </div>
              <div className="app-picker-grid">
                {pickerApps.system.map((item) => (
                  <PickerTile
                    key={itemKey(item.layoutItem, item.id)}
                    item={item}
                    selected={selected?.id === item.id}
                    onSelect={setSelected}
                  />
                ))}
              </div>
              {pickerApps.catalog.length ? (
                <>
                  <div className="profile-restriction-group mt-3">Katalog</div>
                  <div className="app-picker-grid">
                    {pickerApps.catalog.map((item) => (
                      <PickerTile
                        key={itemKey(item.layoutItem, item.id)}
                        item={item}
                        selected={selected?.id === item.id}
                        onSelect={setSelected}
                      />
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : (
            <>
              {pickerWebclips.length === 0 ? (
                <div className="form-text">Keine Webclips in diesem Profil. Zuerst einen Webclip-Payload hinzufügen, dann erscheint er hier.</div>
              ) : (
                <div className="app-picker-grid">
                  {pickerWebclips.map((item) => (
                    <PickerTile
                      key={item.id}
                      item={item}
                      selected={selected?.id === item.id}
                      onSelect={setSelected}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {selected ? (
            <div className="small text-muted mt-2">
              „{selected.name}“ gewählt: auf einen freien Platz im Raster oder Dock klicken oder ziehen.
            </div>
          ) : (
            <div className="small text-muted mt-2">App wählen und auf das iPad ziehen oder antippen.</div>
          )}
        </div>
      </div>

      {folderEdit ? (
        <div className="border rounded p-2 mt-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Ordner: {folderEdit.DisplayName}</strong>
            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setFolderEdit(null)}>Schließen</button>
          </div>
          <input
            className="form-control form-control-sm mb-2"
            disabled={disabled}
            value={folderEdit.DisplayName || ""}
            onChange={(e) => {
              const name = e.target.value;
              const nextPages = pages.map((page) => [...page]);
              const page = [...(nextPages[folderEdit.pageIndex] || [])];
              page[folderEdit.slotIndex] = { ...folderEdit, DisplayName: name };
              nextPages[folderEdit.pageIndex] = page;
              commit(dock, nextPages);
              setFolderEdit({ ...folderEdit, DisplayName: name });
            }}
          />
          <div className="small text-muted">Apps aus der Liste auf den Ordner ziehen, um ihn zu füllen. Zum Entfernen des Ordners das Symbol auf dem Home-Bildschirm anklicken.</div>
        </div>
      ) : null}
    </div>
  );
}
